import React, { useState } from "react";
import { IconManager } from "../core/IconManager/IconManager";
import { ReactComponent as UnreadNotificationIcon } from "../../icons/notificationUnread.svg";
import { ReactComponent as NotificationIcon } from "../../icons/notificationRead.svg";
import { ReactComponent as CheckIcon } from "../../icons/check.svg";
import { FontWeightEnum, IconSizeEnum } from "../../types/AppTypes";
import { SidePanel } from "../core/SidePanel/SidePanel";
import {
  ALL_NOTIFICATIONS_LABEL,
  MARK_ALL_AS_READ_LABEL,
  NO_NOTIFICATION_FOUND_LABEL,
  NOTIFICATION_SEARCH_LABEL,
  NOTIFICATIONS_LABEL,
  READ_NOTIFICATIONS_LABEL,
  UNREAD_NOTIFICATIONS_LABEL,
} from "../../constants/Dictionary";
import { Link } from "../core/Link/Link";
import {
  StyledNotificationPanel,
  StyledNotificationHeader,
} from "./NotificationPanel.styled";
import { TabSwitch, TabSwitchItem } from "../TabSwitch/TabSwitch";
import { OptionItem, TextSwitch } from "../TextSwitch/TextSwitch";
import { BodyTyp } from "../Typography/Typography";
import {
  AppNotification,
  getAppNotificationIcon,
} from "../core/AppNotification/AppNotification";
import SearchInput from "../core/SearchInput/SearchInput";
import { NotificationCategoryEnum } from "../../types/api/notifications.type";
import { InfiniteScrollList } from "../InfiniteScrollList/InfiniteScrollList";
import {
  useNotificationsCounters,
  useNotificationsList,
  useReadAllNotifications,
  useReadSingleNotification,
} from "../../hooks/notificationPanel.hooks";
import {
  NOTIFICATIONS_PAGE_SIZE,
  NotificationsTab,
} from "../../constants/AppConstants";

export const NotificationPanel = () => {
  const [showPanel, setShowPanel] = useState(false);

  const notificationCategories = [
    { value: "ALL", label: ALL_NOTIFICATIONS_LABEL },
    ...Object.values(NotificationCategoryEnum).map((type) => ({
      value: type,
      label: getAppNotificationIcon(type),
    })),
  ];

  const [category, setCategory] = useState<OptionItem<string, React.ReactNode>>(
    notificationCategories[0],
  );

  const { counters } = useNotificationsCounters(showPanel);
  const {
    mutateAsync: readAllNotifications,
    isPending: isPendingAllNotification,
  } = useReadAllNotifications();
  const { mutateAsync: readSingleNotification } = useReadSingleNotification();

  const switchTabs: TabSwitchItem<NotificationsTab>[] = [
    {
      value: NotificationsTab.ALL,
      item: {
        label: ALL_NOTIFICATIONS_LABEL,
        count: counters ? counters.all : 0,
      },
    },
    {
      value: NotificationsTab.UNREAD,
      item: {
        label: UNREAD_NOTIFICATIONS_LABEL,
        count: counters ? counters.unread : 0,
      },
    },
    {
      value: NotificationsTab.READ,
      item: {
        label: READ_NOTIFICATIONS_LABEL,
        count: counters ? counters.read : 0,
      },
    },
  ];

  const [selectedTab, setSelectedTab] = useState<
    TabSwitchItem<NotificationsTab>
  >(switchTabs[0]);

  const {
    notifications,
    initLoading: isLoadingNotificationPanel,
    canLoadMore: loadMoreNotificationPanel,
    fetchMore: fetchMoreNotificationPanel,
    setSearchValue,
    searchValue,
    fetchingMore,
  } = useNotificationsList({
    enabled: showPanel,
    categoryValue: category.value,
    selectedTabValue: selectedTab.value,
    pageSize: NOTIFICATIONS_PAGE_SIZE,
  });

  const notificationsUnreadExist = () => {
    return counters && counters.unread > 0;
  };

  const readAll = async () => {
    await readAllNotifications();
  };

  const readSingle = async (id: number) => {
    await readSingleNotification(id);
  };

  return (
    <>
      <IconManager
        onClick={() => setShowPanel(true)}
        color="none"
        size={IconSizeEnum.LARGE}
      >
        {notificationsUnreadExist() ? (
          <UnreadNotificationIcon />
        ) : (
          <NotificationIcon />
        )}
      </IconManager>
      <SidePanel
        visible={showPanel}
        onClose={() => setShowPanel(!showPanel)}
        label={NOTIFICATIONS_LABEL}
        loading={isLoadingNotificationPanel || isPendingAllNotification}
        footer={
          <Link
            onClick={readAll}
            underline
            icon={<CheckIcon />}
            disabled={isLoadingNotificationPanel || counters?.unread === 0}
          >
            {MARK_ALL_AS_READ_LABEL}
          </Link>
        }
        header={
          <StyledNotificationHeader>
            <TabSwitch
              tabs={switchTabs}
              selectedTab={selectedTab}
              setSelectedTab={setSelectedTab}
            />
            <div className="filters-container">
              <SearchInput
                value={searchValue}
                onChange={(x) => {
                  setSearchValue(x);
                }}
                label={NOTIFICATION_SEARCH_LABEL}
              />
              <TextSwitch
                selectedOption={category}
                setSelectedOption={setCategory}
                options={notificationCategories}
                disabled={isLoadingNotificationPanel || fetchingMore}
                fullWidth
              />
            </div>
          </StyledNotificationHeader>
        }
      >
        <StyledNotificationPanel>
          {notifications.length === 0 ? (
            <div className="empty-notifications">
              <BodyTyp fontWeight={FontWeightEnum.MEDIUM}>
                {NO_NOTIFICATION_FOUND_LABEL}
              </BodyTyp>
            </div>
          ) : (
            <InfiniteScrollList
              items={notifications}
              renderItem={(notification) => (
                <AppNotification
                  key={`notification-${notification.id}`}
                  notification={notification}
                  onRead={() => readSingle(notification.id)}
                />
              )}
              onScroll={fetchMoreNotificationPanel}
              loading={isLoadingNotificationPanel}
              fetchingMore={fetchingMore}
              canLoadMore={loadMoreNotificationPanel}
            />
          )}
        </StyledNotificationPanel>
      </SidePanel>
    </>
  );
};
